<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="content-wrapper" style="margin-bottom: 60px">
      <div class="page-header">{{ $t('termsAndConditions') }}</div>
      <div style="margin-bottom: 40px; margin-left: 5px;">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ name: 'HotelTermsAndConditions' }">{{ $t('termsAndConditions') }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ $t('editTermsAndConditions') }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
      <div class="item" v-for="(term, index) in termsAndConditions.dataList" :key="`${index}-${term.title}`">
        <div class="term-title">{{term.title}}</div>
        <div class="detail">{{getDetail(term)}}</div>
        <div class="footer">
          <el-col :span="12">
            <el-button type="default" size="small" icon="el-icon-top" :disabled="index === 0 " @click="onMoveButtonClick(index, -1)">{{ $t('moveUp') }}</el-button>
            <el-button type="default" size="small" icon="el-icon-bottom" :disabled="index === termsAndConditions.dataList.length - 1 " @click="onMoveButtonClick(index, 1)">{{ $t('moveDown') }}</el-button>
          </el-col>
          <el-col :span="12" style="text-align: right">
            <el-button type="warning" size="small" icon="el-icon-edit" @click="onEditButtonClick(index)"></el-button>
            <el-button type="danger" size="small" icon="el-icon-delete" @click="onDeleteButtonClick(index)"></el-button>
          </el-col>
        </div>
      </div>
      <div class="add-terms-btn" @click="onAddTermsAndConditionButtonClick">
        <i class="el-icon-plus"></i> {{ $t('newTermsAndConditions') }}
      </div>
      <el-row class="edit-remark">
        <el-button type="primary" @click="onSaveChangeButtonClick">{{ $t('saveChange') }}</el-button>
        <el-button type="default" @click="onCancelButtonClick">{{ $t('cancel') }}</el-button>
      </el-row>
    </div>
    <popup-add-edit-terms-and-condition
      :visible="isShowDialog"
      :is-edit-mode="popUpEditMode"
      :data="popupData"
      @onAdded="onAddNewTermAndConditionFromPopup"
      @onSaveChange="onSaveChangeTermAndConditionFromPopup"
      @onClose="onClosePopup"
    ></popup-add-edit-terms-and-condition>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Hotel/ManageHotel/TermsAndConditions/Edit.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import HotelTermsAndConditionsApi from '@/api/hotel/terms-and-conditions-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import PopupAddEditTermsAndCondition from './components/PopupAddEditTermsAndConditions.vue'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { getHotelIndexRoute } from '@/services/hotel-service'
import { bindData } from '@/helpers'

export default {
  name: 'HotelTermsAndConditionsEdit',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer, PopupAddEditTermsAndCondition },
  i18n: { messages: i18nMessages },
  props: {
    termsAndConditions: {
      type: Object,
      default: function () {
        return {
          hotelId: '',
          companyId: '',
          id: '',
          dataList: []
        }
      }
    }
  },
  created: async function () {
    if (!this.termsAndConditions.companyId || !this.termsAndConditions.hotelId) {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }
  },
  data () {
    return {
      isLoading: false,
      isShowDialog: false,
      newTermsAndConditionsData: {
        title: '',
        detail: '',
        order: null
      },
      popUpEditMode: false,
      popupData: {
        title: '',
        detail: ''
      },
      currentUpdatingIndex: -1
    }
  },
  methods: {
    getDetail: function (termAndCondition) {
      return bindData(termAndCondition, 'detail')
    },
    onSaveChangeButtonClick: async function () {
      try {
        this.startLoading()
        const data = {
          dataList: this.termsAndConditions.dataList
        }
        await HotelTermsAndConditionsApi.update(this.termsAndConditions.hotelId, data)
        const title = this.$t('saveSuccessTitle')
        const message = this.$t('saveSuccessMessage')

        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }
      this.stopLoading()
      this.$router.push({
        name: 'HotelTermsAndConditions'
      })
    },
    onCancelButtonClick: function () {
      this.$router.push({
        name: 'HotelTermsAndConditions'
      })
    },
    onAddTermsAndConditionButtonClick: function () {
      this.popUpEditMode = false
      this.popupData.title = ''
      this.popupData.detail = ''
      this.isShowDialog = true
    },
    onAddNewTermAndConditionFromPopup: function (data) {
      this.termsAndConditions.dataList.push({ ...data })
    },
    onClosePopupButtonClick: function () {
      this.isShowDialog = false
    },
    onMoveButtonClick: function (index, direction) {
      this.termsAndConditions.dataList[index].order = direction
      const swapList = [...this.termsAndConditions.dataList]

      const selectItem = swapList[index]
      swapList[index] = swapList[index + direction]
      swapList[index + direction] = selectItem

      this.termsAndConditions.dataList = [...swapList]
    },
    onDeleteButtonClick: async function (index) {
      const confirmMessage = this.$t('confirmMessage', {
        title: this.termsAndConditions.dataList[index]['title']
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }
      this.deleteTermAndCondition(index)
    },
    onEditButtonClick: function (index) {
      this.currentUpdatingIndex = index
      this.popupData.title = this.termsAndConditions.dataList[index].title
      this.popupData.detail = this.termsAndConditions.dataList[index].detail
      this.isShowDialog = true
      this.popUpEditMode = true
    },
    onSaveChangeTermAndConditionFromPopup: function (data) {
      this.termsAndConditions.dataList[this.currentUpdatingIndex].title = data.title
      this.termsAndConditions.dataList[this.currentUpdatingIndex].detail = data.detail
    },
    onClosePopup: function () {
      this.isShowDialog = false
    },
    deleteTermAndCondition: function (index) {
      this.termsAndConditions.dataList.splice(index, 1)
    }
  }
}
</script>

<style scoped>
  .item {
    background-color: #f8f9fa;
    color: #2c3e50;
    text-align: left;
    font-size: 14px;
    padding: 15px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .12), 0 0 6px rgba(0, 0, 0, .04);
  }
  .term-title {
    text-transform: uppercase;
    font-size: 16px;
    font-weight: bold;
  }
  .detail {
    min-height: 40px;
    white-space: pre-line;
  }
  .footer {
    margin-top: 10px;
    height: 30px;
    border: solid 0px #e8ecf0;
    border-top-width: 1px;
    padding: 10px 5px 0px 5px;
    margin-left: -10px;
    margin-right: -10px;
  }
  .add-terms-btn {
    border: dashed 1px rgba(64, 204, 127, 0.12);
    background: #c9deef80;
    color: #2376e5;
    padding: 15px;
    margin-bottom: 20px;
    cursor: pointer;
    font-size: 14px;
  }
</style>
